<template>
  <BittsLoading class="team-tables-wrapper" :is-loading="!ready">
    <div>
      <div class="team-tables-wrapper__header-section">
        <h3
          v-if="!hasNewTeamPage"
          class="team-tables-wrapper__title"
          data-testid="team-tables-wrapper-title"
        >
          Team Members
        </h3>
        <BittsSegment
          v-else
          :active-index
          :tabs
          @change:segment="onSegmentChange"
        />
        <div class="interactivity">
          <BittsInput
            v-model="search"
            type="search"
            data-testid="team-tables-wrapper-search"
            size="small"
            placeholder="Search for users"
            name="team-search"
            class="min-w-[240px] mt-1"
          />
          <div v-if="!selectedCount" class="flex items-center gap-x-6">
            <component
              :is="invitesDisabledReason ? UpsellTooltip : 'div'"
              :hide-upsell="!canEditUsers || isSubscriptionCancelled"
              :skip-modal="hasNewTeamPage"
              :data-testid="
                invitesDisabledReason
                  ? 'invite-disabled-tooltip'
                  : 'invite-button'
              "
              overlay-class="team-tables-wrapper-disabled-tooltip"
              :button-text="inviteTooltipButtonText"
              :cta-icon="
                inviteTooltipButtonText === 'Add seats'
                  ? ['fak', 'add']
                  : ['fak', 'upgrade']
              "
              placement="bottomLeft"
              :billing-interaction="{
                cta: isFreeTier
                  ? TEAM_CTAS.FREE_NEEDS_MORE_SEATS
                  : TEAM_CTAS.BUY_SEATS,
                event_site: EVENT_SITES.TEAM_TABLE_UPSELL_TOOLTIP,
                talkToSalesReason: mustTalkToSales && 'Need more seats',
              }"
            >
              <template #title>
                <span data-testid="invites-disabled-reason">
                  {{ invitesDisabledReason }}
                </span>
              </template>
              <span>
                <BittsButton
                  text="Invite user"
                  :disabled="!!invitesDisabledReason"
                  type="primary"
                  size="small"
                  data-testid="invite-user"
                  :left-icon="['fak', 'add']"
                  @click="handleInvite"
                />
              </span>
            </component>
          </div>
          <div v-else class="bulk-actions">
            <BittsButton
              text="Edit users"
              variant="outline"
              type="neutral"
              size="small"
              :left-icon="['fak', 'edit']"
              @click="handleEdit"
            />
            <component
              :is="deleteDisabled ? BittsTooltip : 'div'"
              placement="topLeft"
            >
              <BittsButton
                text="Delete users"
                :disabled="deleteDisabled"
                variant="outline"
                type="danger"
                size="small"
                :left-icon="['fak', 'delete']"
                @click="handleDelete"
              />
              <template #title>
                {{ TOOLTIP_SELF }}
              </template>
            </component>
          </div>
        </div>
      </div>
      <TeamTable
        v-if="currentView === TEAM_MEMBERS || !hasNewTeamPage"
        :search
        @update:selected-user-ids="(ids) => (selectedUserIds = ids)"
      />
      <SeatRequestsTable
        v-else
        :is-new-team-page="hasNewTeamPage"
        :wrapper-search="search"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsInput,
  BittsLoading,
  BittsSegment,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES, TEAM_CTAS } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed, ref, watchEffect } from 'vue';

import UpsellTooltip from '@/components/billing/UpsellTooltip.vue';
import TeamTable from '@/components/team/TeamTable.vue';

import useTeamInvites from '@/composables/team/useTeamInvites';
import useTeamTable from '@/composables/team/useTeamTable';
import { TOOLTIP_SELF } from '@/constants/team';
import {
  allReady,
  useBillingStore,
  useRolesStore,
  useSeatRequestsStore,
  useTeamStore,
} from '@/stores';

import SeatRequestsTable from './seat-requests/SeatRequestsTable.vue';

const { hasNewTeamPage = false } = defineProps<{
  hasNewTeamPage?: boolean;
}>();

const billingStore = useBillingStore();
const teamStore = useTeamStore();
const rolesStore = useRolesStore();
const seatRequestsStore = useSeatRequestsStore();

const { isFreeTier, isSubscriptionCancelled } = storeToRefs(billingStore);
const { fullAccessRequests, salesAccessRequests } =
  storeToRefs(seatRequestsStore);

const ready = allReady(teamStore, rolesStore, billingStore, seatRequestsStore);

const TEAM_MEMBERS = 'team-members';
const SEAT_REQUESTS = 'seat-requests';
const seatRequestCount = computed(() => {
  return fullAccessRequests.value.length + salesAccessRequests.value.length;
});
const tabs = computed(() => {
  return [
    {
      title: 'Team Members',
      id: TEAM_MEMBERS,
      disabled: false,
    },
    {
      title: 'Seat Requests',
      id: SEAT_REQUESTS,
      disabled: !seatRequestCount.value,
      count: seatRequestCount.value || undefined,
      tooltipProps: !seatRequestCount.value
        ? {
            tooltipTitle: 'There are no current seat requests',
            placement: 'bottomLeft',
          }
        : undefined,
    },
  ];
});
const currentView = ref(TEAM_MEMBERS);
const activeIndex = computed(() =>
  tabs.value.findIndex((tab) => tab.id === currentView.value),
);
function onSegmentChange(segment: string) {
  currentView.value = segment;
  search.value = '';
  selectedUserIds.value = new Set();
}
watchEffect(() => {
  if (
    !seatRequestCount.value &&
    currentView.value === SEAT_REQUESTS &&
    hasNewTeamPage
  ) {
    currentView.value = TEAM_MEMBERS;
  }
});

const {
  canEditUsers,
  deleteDisabled,
  handleDelete,
  handleEdit,
  selectedCount,
  selectedUserIds,
} = useTeamTable();

const {
  invitesDisabledReason,
  inviteTooltipButtonText,
  mustTalkToSales,
  handleInvite,
} = useTeamInvites();

const search = ref('');
</script>

<style lang="pcss" scoped>
.team-tables-wrapper__header-section {
  @apply flex justify-between items-center mb-16;
  .team-tables-wrapper__title {
    @apply text-neutral-text-strong text-lg font-bold;
  }
}

.team-tables-wrapper {
  @apply overflow-hidden mt-40;

  .bulk-actions,
  .interactivity {
    @apply flex justify-between items-end gap-8 ml-8;
  }
}
</style>
